import React, { useState } from "react";
import { Music, KeyRound } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import api from "./api";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong. Please try again.";
}

export default function ResetPassword({ token, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters.");
    if (pw !== pw2) return setErr("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
            <Music className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900 leading-tight">Melody Teacher</h1>
            <p className="text-xs text-slate-500">Music Class Manager</p>
          </div>
        </div>

        {done ? (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-4 text-center" data-testid="reset-done">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
              <KeyRound className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Password updated!</h2>
              <p className="text-xs text-slate-500 mt-1">You can now sign in with your new password.</p>
            </div>
            <Button onClick={onDone} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="reset-goto-login-btn">
              Go to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-5" data-testid="reset-form">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Choose a new password</h2>
              <p className="text-xs text-slate-500 mt-1">At least 6 characters. Reset links expire after 1 hour.</p>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">New password</label>
              <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                className="h-11 border-slate-200" data-testid="reset-pw-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Confirm new password</label>
              <Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="h-11 border-slate-200" data-testid="reset-pw2-input" />
            </div>

            {err && (
              <div className="text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-error">
                {err}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="reset-submit-btn">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
